import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import Eyebrow from "@/components/site/Eyebrow";
import { ACCENT, NAVY, CREAM, NAVY_PANEL } from "@/lib/siteTheme";
import heronIcon from "@/assets/design/blue-heron-icon.webp";
import lukeHero from "@/assets/design/luke.webp";
import lukeStory from "@/assets/design/luke-story.webp";

const industries = [
  "Chiropractors & Healthcare",
  "HVAC & Home Services",
  "Auto Repair & Automotive",
  "Insurance Agencies",
  "Senior Living Communities",
  "Professional Services",
];

const education = [
  { degree: "Master's Degree", field: "Integrated Marketing Communications", school: "University of Kansas" },
  { degree: "Bachelor's Degree", field: "PR / Marketing", school: "Middle Tennessee State University" },
];

const experience = [
  { role: "Founder", org: "Blue Heron", years: "2014 – Present" },
  { role: "Director of Sales", org: "SeniorLivingPROS", years: "2021 – Present" },
  { role: "Director of Integrated Marketing", org: "Pivotal Health Care", years: "2020 – 2021" },
  { role: "Integrated Marketing Specialist", org: "Pivotal Health Care", years: "2017 – 2020" },
];

const principles = [
  { title: "Transparency", desc: "No hidden fees, no confusing reports. You always know what I’m doing and why." },
  { title: "Results Over Activity", desc: "I don't bill for busy work, only activities that directly contribute to your growth." },
  { title: "Partnership", desc: "Your success is my success. I'm invested in your long-term growth, not just monthly retainers." },
];


const About = () => {
  return (
    <Layout active="about" background={NAVY}>
      <div style={{ fontFamily: "'Archivo', sans-serif", background: NAVY }} className="bh-about w-full antialiased">
      <Seo
        title="About Luke | Blue Heron Marketing"
        description="Meet Luke, founder of Blue Heron Marketing. Learn how we help local businesses grow with honest, results-driven digital marketing."
        path="/about"
      />

      <div id="top" style={{ background: CREAM, color: "#11212F" }}>
        {/* Hero */}
        <section className="relative overflow-hidden px-6 md:px-14 py-24 md:py-[120px]" style={{ background: NAVY }}>
          <div
            className="absolute -right-[140px] -bottom-10 w-[600px] max-w-[55vw] opacity-[0.06] pointer-events-none"
            style={{ transform: "scaleX(-1)" }}
          >
            <img
              src={heronIcon}
              alt=""
              width={500}
              height={366}
              className="w-full h-auto block brightness-0 invert"
              style={{ animation: "bhFloat 11s ease-in-out infinite" }}
            />
          </div>
          <div className="max-w-[1280px] mx-auto relative z-10 flex flex-col lg:flex-row gap-12 lg:gap-[72px] items-center">
            <div className="flex-[1.2] min-w-[280px]">
              <Eyebrow dark>About</Eyebrow>
              <h1
                className="font-black leading-[0.98] tracking-[-0.02em] mb-6"
                style={{ color: CREAM, fontSize: "clamp(44px,6.5vw,84px)" }}
              >
                Hi, I'm Luke.
              </h1>
              <p className="max-w-[540px] mb-9 leading-relaxed" style={{ color: "rgba(247,243,236,0.78)", fontSize: "clamp(17px,1.6vw,20px)" }}>
                I help local business owners grow through marketing strategies that actually work, no fluff, no guesswork, just data-driven results.
              </p>
              <div className="flex flex-wrap gap-3.5">
                {[
                  { value: "10+", label: "Years Experience" },
                  { value: "5.0★", label: "Google Rating" },
                  { value: "Kansas City", label: "Based" },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-2xl px-[22px] py-3.5" style={{ background: "rgba(247,243,236,0.08)", border: "1px solid rgba(247,243,236,.18)" }}>
                    <div className="font-black text-2xl" style={{ color: ACCENT }}>{stat.value}</div>
                    <div className="text-xs font-bold tracking-[0.06em] uppercase mt-0.5" style={{ color: "rgba(247,243,236,0.6)" }}>{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-1 min-w-[260px] relative w-full rounded-[28px] overflow-hidden" style={{ aspectRatio: "4/5", background: NAVY_PANEL }}>
              <img src={lukeHero} alt="Luke, founder of Blue Heron" className="w-full h-full object-cover block" />
            </div>
          </div>
        </section>

        {/* My Story */}
        <section className="px-6 md:px-14 py-24 md:py-[130px]">
          <div className="max-w-[1280px] mx-auto flex flex-col lg:flex-row gap-12 lg:gap-[72px] items-center">
            <div className="flex-1 min-w-[260px] relative w-full rounded-[28px] overflow-hidden" style={{ aspectRatio: "1/1", background: NAVY_PANEL }}>
              <img src={lukeStory} alt="Luke" className="w-full h-full object-cover block" />
            </div>
            <div className="flex-[1.2] min-w-[280px]">
              <Eyebrow>My Story</Eyebrow>
              <h2 className="font-black leading-[0.98] tracking-[-0.025em] mb-6" style={{ color: NAVY, fontSize: "clamp(30px,4vw,46px)" }}>
                How I got here.
              </h2>
              <div className="space-y-[18px] text-[17px] leading-relaxed" style={{ color: "rgba(17,33,47,0.72)" }}>
                <p>I'm the founder of Blue Heron, a marketing consultancy based in Kansas City. I hold a bachelor's degree in PR/Marketing from Middle Tennessee State University and a master's in Integrated Marketing Communications from the University of Kansas.</p>
                <p>But here's what matters more than the degrees: I've spent over a decade in the trenches of digital marketing, building campaigns, analyzing data, and learning what actually drives results for local businesses.</p>
                <p>Before starting Blue Heron, I founded SeniorLivingPROS, a marketing automation company, and served as Director of Sales there for over four years. I've also held leadership roles at Pivotal Health Care and Grand Digital, developing marketing strategies for businesses across the Midwest, and discovered I get the most satisfaction from helping local owners turn strategy into real, measurable success.</p>
              </div>
            </div>
          </div>
        </section>

        {/* My Approach */}
        <section className="px-6 md:px-14 py-24 md:py-[130px]" style={{ background: NAVY }}>
          <div className="max-w-[1280px] mx-auto">
            <Eyebrow dark>My Approach</Eyebrow>
            <h2 className="font-black leading-[0.98] tracking-[-0.025em] max-w-[640px] mb-16" style={{ color: CREAM, fontSize: "clamp(30px,4vw,48px)" }}>
              How I work differently.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
              <div className="bg-white rounded-3xl px-8 py-9">
                <h3 className="font-extrabold text-xl mb-3.5" style={{ color: NAVY }}>Every Business Is Different</h3>
                <p className="text-[15.5px] leading-relaxed" style={{ color: "rgba(17,33,47,0.65)" }}>
                  Most agencies sell one-size-fits-all packages. I don't. A chiropractor's marketing needs are nothing like an HVAC company's, different search intent, different customer journey, different conversion points. Every proposal is built for your industry and goals.
                </p>
              </div>
              <div className="bg-white rounded-3xl px-8 py-9">
                <h3 className="font-extrabold text-xl mb-3.5" style={{ color: NAVY }}>It's Not Just About Leads</h3>
                <p className="text-[15.5px] leading-relaxed" style={{ color: "rgba(17,33,47,0.65)" }}>
                  Most owners say they want more leads, and they do. But what they really need is leads plus a system to nurture them into customers. That's where most marketing fails. I build both: the generation and the framework to convert.
                </p>
              </div>
              <div className="rounded-3xl px-8 py-9" style={{ background: ACCENT }}>
                <h3 className="font-extrabold text-xl mb-3.5" style={{ color: NAVY }}>No Fluff. Just Results.</h3>
                <p className="text-[15px] leading-snug italic mb-4" style={{ color: "rgba(14,40,70,0.8)" }}>
                  "People don't buy the best products. They buy the products they can understand the fastest." — Donald Miller, StoryBrand
                </p>
                <p className="text-[15.5px] leading-relaxed" style={{ color: "rgba(14,40,70,0.8)" }}>
                  I don't rely on feelings about growth. I rely on data, cutting through the noise to find what actually drives results for your business.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Important callout */}
        <section className="px-6 md:px-14 py-20" style={{ background: ACCENT }}>
          <div className="max-w-[880px] mx-auto text-center">
            <div className="font-extrabold text-xs tracking-[0.14em] uppercase mb-4" style={{ color: "rgba(14,40,70,0.6)" }}>Important</div>
            <h2 className="font-black leading-[1.08] tracking-[-0.02em] mb-5" style={{ color: NAVY, fontSize: "clamp(26px,3.4vw,40px)" }}>
              The biggest mistake local businesses make.
            </h2>
            <p className="text-[17px] leading-relaxed" style={{ color: "rgba(14,40,70,0.78)" }}>
              A business owner hires a large, generic agency, pays $3,000+ for a website, and expects it to magically bring in business. It doesn't work that way. A website is just a tool, it needs strategy behind it: SEO to drive traffic, content that converts, and a system to capture and follow up with leads. Without that foundation, you're just burning money.
            </p>
          </div>
        </section>

        {/* Working Together */}
        <section className="px-6 md:px-14 py-24 md:py-[130px]">
          <div className="max-w-[1280px] mx-auto">
            <Eyebrow>Working Together</Eyebrow>
            <h2 className="font-black leading-[0.98] tracking-[-0.025em] max-w-[640px] mb-16" style={{ color: NAVY, fontSize: "clamp(30px,4vw,48px)" }}>
              What working with me looks like.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
              <div className="bg-white rounded-3xl px-8 py-9" style={{ border: "1px solid rgba(14,40,70,.08)" }}>
                <h3 className="font-extrabold text-xl mb-3.5" style={{ color: NAVY }}>Hands-On Partnership</h3>
                <p className="text-[15.5px] leading-relaxed" style={{ color: "rgba(17,33,47,0.65)" }}>
                  I'm hands-on with every client. Whether I'm building your Google Ads campaign or serving as your fractional CMO, I'm in the details with you, no junior hand-offs, no disappearing after the first call.
                </p>
              </div>
              <div className="bg-white rounded-3xl px-8 py-9" style={{ border: "1px solid rgba(14,40,70,.08)" }}>
                <h3 className="font-extrabold text-xl mb-3.5" style={{ color: NAVY }}>Clear Goals, Measurable Results</h3>
                <p className="text-[15.5px] leading-relaxed" style={{ color: "rgba(17,33,47,0.65)" }}>
                  Success looks like meeting the goals we set together, 20 new appointments a month, doubling service calls, or a predictable pipeline of leads. We set KPIs from day one and adjust based on the data.
                </p>
              </div>
              <div className="bg-white rounded-3xl px-8 py-9" style={{ border: "1px solid rgba(14,40,70,.08)" }}>
                <h3 className="font-extrabold text-xl mb-4" style={{ color: NAVY }}>Who I Work With</h3>
                <div className="flex flex-wrap gap-2">
                  {industries.map((industry) => (
                    <span key={industry} className="rounded-full px-3 py-1.5 font-bold text-[12.5px]" style={{ background: "rgba(14,40,70,0.06)", color: NAVY }}>
                      {industry}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Background */}
        <section className="px-6 md:px-14 py-24 md:py-[130px]" style={{ background: NAVY }}>
          <div className="max-w-[1280px] mx-auto">
            <Eyebrow dark>Background</Eyebrow>
            <h2 className="font-black leading-[0.98] tracking-[-0.025em] max-w-[640px] mb-16" style={{ color: CREAM, fontSize: "clamp(30px,4vw,48px)" }}>
              Education &amp; experience.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
              <div>
                <div className="font-extrabold text-[13px] tracking-[0.1em] uppercase mb-6" style={{ color: ACCENT }}>Education</div>
                <div className="flex flex-col gap-6">
                  {education.map((ed) => (
                    <div key={ed.degree} className="pt-5" style={{ borderTop: "1px solid rgba(247,243,236,0.15)" }}>
                      <div className="font-extrabold text-lg" style={{ color: CREAM }}>{ed.degree}</div>
                      <div className="text-[15px] mt-1" style={{ color: "rgba(247,243,236,0.65)" }}>{ed.field}</div>
                      <div className="text-sm mt-0.5" style={{ color: "rgba(247,243,236,0.45)" }}>{ed.school}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="font-extrabold text-[13px] tracking-[0.1em] uppercase mb-6" style={{ color: ACCENT }}>Experience</div>
                <div className="flex flex-col gap-5">
                  {experience.map((exp) => (
                    <div key={exp.role + exp.org} className="pt-4 flex justify-between gap-4 items-baseline" style={{ borderTop: "1px solid rgba(247,243,236,0.15)" }}>
                      <div>
                        <div className="font-extrabold text-base" style={{ color: CREAM }}>{exp.role}</div>
                        <div className="text-sm mt-0.5" style={{ color: "rgba(247,243,236,0.55)" }}>{exp.org}</div>
                      </div>
                      <div className="text-[13px] font-bold whitespace-nowrap" style={{ color: "rgba(247,243,236,0.4)" }}>{exp.years}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Personal */}
        <section className="px-6 md:px-14 py-24 md:py-[130px]">
          <div className="max-w-[1280px] mx-auto flex flex-col lg:flex-row gap-12 lg:gap-[72px] items-start">
            <div className="flex-[1.1] min-w-[280px]">
              <Eyebrow>Personal</Eyebrow>
              <h2 className="font-black leading-[0.98] tracking-[-0.025em] mb-6" style={{ color: NAVY, fontSize: "clamp(28px,3.6vw,42px)" }}>
                Beyond business.
              </h2>
              <div className="space-y-4 text-[17px] leading-relaxed" style={{ color: "rgba(17,33,47,0.72)" }}>
                <p>I live in Kansas City with my wife Bailey and our goldendoodle, Paddington. When I'm not building marketing strategies, you'll find me maintaining my planted aquarium, golfing, or involved in my local church community.</p>
                <p className="font-medium" style={{ color: NAVY }}>
                  I believe in building authentic relationships with clients, not just transactional arrangements. Your success matters to me, and I treat your marketing budget like it's my own.
                </p>
              </div>
            </div>
            <div className="flex-1 min-w-[260px] flex flex-col gap-5">
              {principles.map((p) => (
                <div key={p.title} className="bg-white rounded-2xl px-[26px] py-6" style={{ border: "1px solid rgba(14,40,70,.08)" }}>
                  <div className="font-extrabold text-base mb-1.5" style={{ color: NAVY }}>{p.title}</div>
                  <div className="text-[14.5px] leading-snug" style={{ color: "rgba(17,33,47,0.62)" }}>{p.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="px-6 md:px-14 pt-[110px] pb-20 text-center">
          <div className="max-w-[760px] mx-auto">
            <h2 className="font-black leading-[0.98] tracking-[-0.025em] mb-5" style={{ color: NAVY, fontSize: "clamp(30px,4.4vw,52px)" }}>
              Let's build something that works.
            </h2>
            <p className="mb-8 text-lg leading-relaxed" style={{ color: "rgba(17,33,47,0.65)" }}>
              Ready to stop wasting money on marketing that doesn't deliver? Let's talk about what's actually possible for your business.
            </p>
            <div className="flex flex-wrap gap-3.5 justify-center mb-9">
              <span className="rounded-full px-[18px] py-2.5 font-bold text-[13px]" style={{ background: "rgba(14,40,70,0.06)", color: NAVY }}>
                Free 30-Minute Consultation
              </span>
              <span className="rounded-full px-[18px] py-2.5 font-bold text-[13px]" style={{ background: "rgba(14,40,70,0.06)", color: NAVY }}>
                No Pressure, No Sales Pitch
              </span>
            </div>
            <Link
              to="/book"
              className="inline-flex items-center gap-2.5 font-extrabold text-lg px-[38px] py-5 rounded-full no-underline transition-transform hover:-translate-y-1"
              style={{ background: ACCENT, color: NAVY, boxShadow: "0 16px 32px rgba(14,40,70,.16)" }}
            >
              Book A Free Call →
            </Link>
          </div>
        </section>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .bh-about h1, .bh-about h2, .bh-about h3, .bh-about h4 {
          font-family: 'Archivo', sans-serif;
        }
        @keyframes bhFloat {
          0% { transform: translateY(0) rotate(-6deg); }
          50% { transform: translateY(-22px) rotate(-4deg); }
          100% { transform: translateY(0) rotate(-6deg); }
        }
      ` }} />
      </div>
    </Layout>
  );
};

export default About;
